import type { PanelSenderSessionRow } from '@/api/sender-sessions';
import type { SenderChipTone } from '@/pages/sender/components/sender-status-chip';

/** Mirrors back export + bulk-spam max session count. */
export const PANEL_SENDER_SESSIONS_BULK_MAX = 100;

export const SESSION_DISPLAY_STATUS_LABEL: Record<PanelSenderSessionRow['displayStatus'], string> = {
	ready: 'Готов',
	running: 'В работе',
	archived: 'В архиве',
	pending: 'Ожидание',
	failed: 'Ошибка',
	expired: 'Истёк',
};

export function sessionDisplayStatusLabel(
	displayStatus: PanelSenderSessionRow['displayStatus'],
): string {
	return SESSION_DISPLAY_STATUS_LABEL[displayStatus] ?? 'Статус';
}

export function sessionDisplayStatusToChipTone(
	displayStatus: PanelSenderSessionRow['displayStatus'],
): SenderChipTone {
	switch (displayStatus) {
		case 'running':
			return 'primary';
		case 'ready':
			return 'success';
		case 'failed':
		case 'expired':
			return 'destructive';
		case 'pending':
			return 'warning';
		case 'archived':
		default:
			return 'muted';
	}
}

export function sessionPrimaryTitle(row: PanelSenderSessionRow): string {
	return row.label?.trim() || row.username?.trim() || row.phoneMasked;
}

export function sessionSecondaryLine(row: PanelSenderSessionRow): string | null {
	const title = sessionPrimaryTitle(row);
	const parts: string[] = [];
	if (title !== row.phoneMasked) {
		parts.push(row.phoneMasked);
	}
	if (row.label?.trim() && row.username?.trim()) {
		const handle = row.username.startsWith('@') ? row.username : `@${row.username}`;
		parts.push(handle);
	}
	if (parts.length === 0) return null;
	return parts.join(' · ');
}

export function formatSessionWhen(row: PanelSenderSessionRow): string {
	const raw = row.authorizedAt ?? row.createdAt;
	return new Date(raw).toLocaleString('ru-RU');
}

export function mergePanelSenderSessions(
	ready: readonly PanelSenderSessionRow[],
	archived: readonly PanelSenderSessionRow[],
): PanelSenderSessionRow[] {
	const byId = new Map<string, PanelSenderSessionRow>();
	for (const row of [...ready, ...archived]) {
		byId.set(row.id, row);
	}
	return [...byId.values()].sort(
		(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
	);
}

export function sessionMatchesQuery(row: PanelSenderSessionRow, query: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	const haystack = [row.phoneMasked, row.label, row.username]
		.filter(Boolean)
		.join(' ')
		.toLowerCase();
	return haystack.includes(q);
}

export function selectedIncludesArchived(
	sessions: readonly PanelSenderSessionRow[],
	selectedIds: readonly string[],
): boolean {
	const selected = new Set(selectedIds);
	return sessions.some(s => selected.has(s.id) && s.displayStatus === 'archived');
}

export function selectedNonArchivedIds(
	sessions: readonly PanelSenderSessionRow[],
	selectedIds: readonly string[],
): string[] {
	const selected = new Set(selectedIds);
	return sessions
		.filter(s => selected.has(s.id) && s.displayStatus !== 'archived')
		.map(s => s.id);
}

export function capSessionIds(
	ids: readonly string[],
	max = PANEL_SENDER_SESSIONS_BULK_MAX,
): string[] {
	return ids.slice(0, max);
}

export function visibleIdsForSelectAll(visible: readonly PanelSenderSessionRow[]): {
	ids: string[];
	capped: boolean;
} {
	const ids = visible.map(s => s.id);
	return {
		ids: capSessionIds(ids),
		capped: ids.length > PANEL_SENDER_SESSIONS_BULK_MAX,
	};
}

export function selectionOverBulkLimit(count: number): boolean {
	return count > PANEL_SENDER_SESSIONS_BULK_MAX;
}

export function formatAccountsDeletePrompt(count: number): string {
	if (count === 1) return 'Удалить 1 аккаунт?';
	if (count >= 2 && count <= 4) return `Удалить ${count} аккаунта?`;
	return `Удалить ${count} аккаунтов?`;
}
